import React, { useCallback, useEffect, useState } from 'react';
import { XGrid } from '@AMIEWEB/Common';
import { orderTargetMarginGridColumns } from './OrderTargetMarginsGridUtils';
import { useTranslation } from 'react-i18next';
import { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { useDispatch, useSelector } from 'react-redux';
import { IOrderTargetMargin, IUpdateOrderTargetMargin, orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { usePromiseTracker } from 'react-promise-tracker';
import {
  selectConfirmationDialog,
  selectDirtyRows,
  selectOrderPayPackagesInteraction,
  selectPreferenceChanged,
  selectTabValue,
  selectTargetMargins,
} from '@AMIEWEB/Order/Store/Order.selectors';
import { Controller, useFormContext } from 'react-hook-form';
import { ActionButtonWrapper } from '@AMIEWEB/Common/SubmissionPreference/ActionButtonWrapper/ActionButtonWrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { selectUser } from 'oidc/user.selectors';
import { ConfirmationDialog } from '@AMIEWEB/Settings/AutoTaskManagement/common/ConfirmationDialog';
import { useConfirmExit } from 'utils/customHooks/useConfirmExit';
import { INavigation, navigationStickActions } from '@AMIEWEB/GlobalNavigationMenu/NavigationStick.redux';
import { useReadOnly, userRoles } from 'oidc/userRoles';
import { coerceRowValues } from './OrderTargetMarginHelper';
import { FormNames } from '@AMIEWEB/Common/SubmissionPreference/Common/helper';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { CircularProgress, Stack } from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { selectGridSelection } from '@AMIEWEB/Common/Grid/GridSelection/GridSelection.selector';
import { OrderPayPackagesAction } from '@AMIEWEB/Order/Store/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(themes => ({
  highlightRow: {
    backgroundColor: 'rgba(0, 111, 185, 0.08)' + ' !important',
  },
}));

export const OrderTargetMarginsGrid = ({ apiRef }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const orderPayPackagesInteraction = useSelector(selectOrderPayPackagesInteraction);
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();
  const user = useSelector(selectUser);
  const dirtyRows = useSelector(selectDirtyRows);
  const { selectedData } = useSelector(selectGridSelection);
  const [columns] = useState<any>(orderTargetMarginGridColumns());
  const { blockExit, allowExit } = useConfirmExit();
  const tabValue = useSelector(selectTabValue);
  const confirmationDialog = useSelector(selectConfirmationDialog);
  const preferenceChanged = useSelector(selectPreferenceChanged);
  const { readOnly } = useReadOnly()
  const [openClearChangesConfirmation, setOpenClearChangesConfirmation] = useState(false);
  const [triggerExit, setTriggerExit] = useState({
    isExit: false,
    path: '',
  });

  const {
    watch,
    formState: { dirtyFields },
  } = useFormContext();
  const isTargetMarginDirty = Boolean(dirtyFields[FormNames.TargetMargins]);

  const rows = watch(FormNames.TargetMargins) as IOrderTargetMargin[];
  const { promiseInProgress: resetPromiseInProgress } = usePromiseTracker({
    area: `track-get-target-margin-reset-values`,
    delay: 0,
  });

  const isCellEditable = React.useCallback(
    (params: any) => {
      const isResetActionTriggered = orderPayPackagesInteraction.action === OrderPayPackagesAction.TARGET_MARGIN_RESET;
      if (isResetActionTriggered || resetPromiseInProgress) {
        return false; //Dont allow editing when reset action is triggered
      }
      const editableFields = new Set([
        'targetMarginDefault',
        'targetMarginDayShift',
        'targetMarginNightShift',
        'targetMarginDayNightShift',
        'payRangeMinMaxPercentage',
        'defaultWeeklyPay',
      ]);
      const selectRowDetail = rows.find(row => row.rowId === params.id);
      return !!selectRowDetail?.rowId && editableFields.has(params.field);
    },
    [rows, orderPayPackagesInteraction, resetPromiseInProgress],
  );

  const { promiseInProgress: isLoadingTargetMargins } = usePromiseTracker({
    area: 'get-order-target-margins',
    delay: 0,
  });

  const { promiseInProgress: isSaving } = usePromiseTracker({
    area: `update-order-target-margins`,
    delay: 0,
  });

  const leftActions = React.useMemo<IXGridBulkActionButton[]>(
    () => [
      // @ts-ignore
      {
        title: t('Reset Target Margins'),
        Icon: () => (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReplayRoundedIcon />
            {resetPromiseInProgress && <CircularProgress size={14} />}
          </Stack>
        ),
        disabled: selectedData.length === 0 || resetPromiseInProgress || isTargetMarginDirty || readOnly,
        onClick: () => {
          const updatedData = rows.map<IOrderTargetMargin>(row => {
            return selectedData.includes(row.rowId)
              ? { ...row, mode: OrderPayPackagesAction.TARGET_MARGIN_RESET }
              : row;
          });
          dispatch(orderDataActions.getTargetMarginResetValues(updatedData));
        },
        tooltipProps: {
          tooltipContent: t('order.payPackagesTab.orderPayPackages.grid.button.resetPayPackageTooltipsMessage'),
          disabled: selectedData.length <= 5 && selectedData.length > 0,
        },
      },
    ],
    [selectedData, rows, resetPromiseInProgress, isTargetMarginDirty,readOnly],
  );

  return (
    <React.Fragment>
      <Controller
        name={FormNames.TargetMargins}
        render={({ field }) => (
          <XGrid
            {...field}
            getRowId={row => row.rowId}
            key={field.value?.length}
            columns={columns}
            rows={field.value}
            editMode="row"
            apiRef={apiRef}
            checkboxSelection
            loading={isLoadingTargetMargins}
            isCellEditable={params => {
              return !readOnly && isCellEditable(params);
            }}
            processRowUpdate={(updatedRow, oldRow) => {
              const newRows = field.value.map(row =>
                row.rowId === updatedRow.rowId ? { ...updatedRow, ...coerceRowValues(updatedRow) } : row,
              );
              field.onChange(newRows);
              dispatch(orderDataActions.setDirtyRows(updatedRow.rowId));
              return updatedRow;
            }}
            experimentalFeatures={{ newEditingApi: true }}
            height={380}
            pageSize={50}
            rowsPerPageOptions={[50]}
            title={t('order.orderDetails.preferences.targetMargins.title')}
            pagination={false}
            keepNonExistentRowsSelected={true}
            sortingMode="client"
            disableSelectionOnClick={true}
            toolbarProps={{
              leftActions: leftActions,
              refreshButton: true,
              refreshGrid: () => {
                dispatch(
                  orderDataActions.getOrderTargetMargins({
                    payload: {
                      orderId: parseInt(params?.id),
                      userId: user?.userInfo?.employeeId,
                    },
                  }),
                );
                apiRef.current?.setSelectionModel([]); //clear selection in grid
              },
            }}
            getRowClassName={(params: GridRowClassNameParams) =>
              dirtyRows.includes(params.id as number) ? classes.highlightRow : ''
            }
          />
        )}
      />
    </React.Fragment>
  );
};
